/**
 * Activist card screen (/activists/:id) — content (step 5/6).
 *
 * Reads id from the route (no props), like TouchDetail. There is no by-id
 * endpoint upstream (MOB_API_REVERSE p.55): the single activist comes from the
 * listing via useVolunteer (id-among filter).
 *
 * Touch history mirrors mob's own «КАСАНИЯ» tab: the volunteer object carries a
 * read-only `touches` array of touch ids; we fetch their names/types/periods via
 * GET /api/touches?id__among=<ids> (useTouches with an `among` filter). We do NOT
 * use the /history sub-resource — those are foreign rows without touch names.
 *
 * The chrome (AppHeader, sub-header, 1fr/380px body grid, loading / error /
 * not-found) is owned here. The left-column cards (Identity, Contacts, Binding,
 * Flags) and the right column (touch list + Summary) render their content
 * from `volunteerQuery.data` (loosely typed — missing values fall back to "—",
 * nothing is invented) and `touchesQuery.data` ({count, results}).
 */

import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import type { Volunteer, Refs, Touch } from "@/api/client";
import { useVolunteer, useTouches, useRefs } from "@/api/hooks";
import { FIELD_BY_KEY, resolveOptions } from "@/lib/fields";
import { AppHeader } from "@/components/AppHeader";
import { Spinner } from "@/components/ui/spinner";
import { Pill } from "@/components/ui/Pill";
import { READINESS_TONE, LOYALTY_TONE, type Tone } from "@/lib/tones";

const MONO = "'Geist Mono', monospace";
const CARD_STYLE: React.CSSProperties = {
  background: "var(--panel)",
  border: "1px solid var(--border-hex)",
  borderRadius: 14,
  boxShadow: "0 1px 3px rgba(0,0,0,.04)",
};
const SECTION_LABEL_STYLE: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: ".06em",
  textTransform: "uppercase",
  color: "var(--text-muted)",
  padding: "14px 0 8px",
};

/** Pull a readable value out of a loosely typed object, trying candidate keys. */
export function pick(obj: Record<string, unknown> | undefined, keys: string[]): string {
  if (!obj) return "—";
  for (const k of keys) {
    const v = obj[k];
    if (v != null && v !== "") return String(v);
  }
  return "—";
}

/** ISO/date-ish string → дд.мм.гггг, or "—" when unparseable/empty. */
export function fmtDate(iso: unknown): string {
  if (iso == null || iso === "") return "—";
  const d = new Date(String(iso));
  if (Number.isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}.${mm}.${d.getFullYear()}`;
}

/** Read a raw (untyped) field off the loosely-typed volunteer object. */
function raw(v: Volunteer | undefined, key: string): unknown {
  if (!v) return undefined;
  return (v as Record<string, unknown>)[key];
}

/** Initials from surname+first name; "?" when neither is present. */
function initialsOf(v: Volunteer | undefined): string {
  const a = (v?.last_name ?? "").trim();
  const b = (v?.first_name ?? "").trim();
  const out = `${a.charAt(0)}${b.charAt(0)}`.toUpperCase();
  return out || "?";
}

/** Фамилия + Имя + Отчество, collapsed of blanks; "—" when fully empty. */
function fullName(v: Volunteer | undefined): string {
  const parts = [v?.last_name, v?.first_name, v?.patronymic]
    .map((p) => (p ?? "").toString().trim())
    .filter(Boolean);
  return parts.length ? parts.join(" ") : "—";
}

/** Coerce an unknown list-ish value into a string[] (handles array | scalar). */
function toList(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((x) => String(x)).filter(Boolean);
  if (value == null || value === "") return [];
  return [String(value)];
}

/** Loyalty → tone (defaults to gray for unknown labels). */
function loyaltyTone(label: string): Tone {
  return LOYALTY_TONE[label] ?? "gray";
}

/**
 * Membership code (-/f/m) → human label, resolved through the SAME mechanism
 * the «Членство» facet/filter uses: resolveOptions() against the `membership`
 * field def. Upstream /api/refs.membership is a {code: label} dict (see
 * lib/fields.ts resolveOptions), so the label must be looked up by code — NOT
 * by array index. resolveOptions yields [{value: code, label}], which we turn
 * into a code→label map. Empty / "-" → «—».
 */
function membershipLabel(v: Volunteer | undefined, refs: Refs | undefined): string {
  const code = raw(v, "membership");
  if (Array.isArray(code)) {
    const joined = code.map((c) => String(c)).filter(Boolean);
    return joined.length ? joined.join(", ") : "—";
  }
  if (code == null || code === "" || code === "-") return "—";
  const c = String(code);
  const def = FIELD_BY_KEY.membership;
  if (def) {
    const byCode = new Map(resolveOptions(def, refs).map((o) => [o.value, o.label]));
    const label = byCode.get(c);
    if (label) return label;
  }
  return c;
}

/** target_group_ids → human labels from refs.targetGroups (id → title|name). */
function targetGroupLabels(v: Volunteer | undefined, refs: Refs | undefined): string[] {
  const ids = toList(raw(v, "target_group_ids"));
  if (!ids.length) return [];
  const byId = new Map<string, string>();
  for (const g of refs?.targetGroups ?? []) {
    byId.set(String(g.id), g.title ?? g.name ?? String(g.id));
  }
  return ids.map((id) => byId.get(id) ?? id);
}

/** "Да"/"Нет"/"—" from a loosely-typed boolean-ish flag. */
function yesNo(value: unknown): string {
  if (value === true || value === "y" || value === "Да" || value === 1 || value === "1") return "Да";
  if (value === false || value === "n" || value === "Нет" || value === 0 || value === "0")
    return "Нет";
  return "—";
}

/** дд.мм.гггг — дд.мм.гггг from a (start, end) pair; "—" when both empty. */
function fmtPeriod(start: unknown, end: unknown): string {
  const s = fmtDate(start);
  const e = fmtDate(end);
  if (s !== "—" && e !== "—") return s === e ? s : `${s} — ${e}`;
  if (s !== "—") return s;
  if (e !== "—") return e;
  return "—";
}

/** Coerce volunteer.touches (ids, possibly strings/mixed) into a clean number[]. */
function touchIdsOf(volunteer: Volunteer | undefined): number[] {
  const raw = (volunteer as Record<string, unknown> | undefined)?.touches;
  if (!Array.isArray(raw)) return [];
  const out: number[] = [];
  for (const v of raw) {
    const n = typeof v === "number" ? v : Number(v);
    if (Number.isFinite(n) && n > 0) out.push(n);
  }
  return out;
}

/** Epoch ms for sorting touches by start date desc; -Infinity when undated. */
function startMs(t: Touch): number {
  const v = t.date_start;
  if (v == null || v === "") return Number.NEGATIVE_INFINITY;
  const d = new Date(String(v)).getTime();
  return Number.isNaN(d) ? Number.NEGATIVE_INFINITY : d;
}

export function ActivistCard(): JSX.Element {
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const id = Number(params.id);

  // Primary source: the full Volunteer row handed over by the table on row-click
  // (navigate(..., { state: { volunteer } })). mob has no by-id endpoint, so when
  // we already hold the object we render it directly — no network round-trip.
  const stateVolunteer = (location.state as { volunteer?: Volunteer } | null)?.volunteer;

  // Fallback only for a cold entry (direct URL / reload), where there is no state.
  const volunteerQuery = useVolunteer(id, !stateVolunteer);
  const refsQuery = useRefs();

  const volunteer: Volunteer | undefined = stateVolunteer ?? volunteerQuery.data;
  const refs: Refs | undefined = refsQuery.data;

  // Touch history, the way mob does it: volunteer.touches is a read-only array
  // of touch ids; resolve names/types/periods via id__among (no /history).
  const touchIds = touchIdsOf(volunteer);
  const touchesQuery = useTouches(
    { page: 1, pageSize: 100, filters: [{ field: "id", operator: "among", value: touchIds }] },
    touchIds.length > 0,
  );
  const touches: Touch[] = touchesQuery.data?.results ?? [];

  if (!Number.isFinite(id)) {
    return (
      <div className="flex h-screen flex-col" data-testid="activist-card">
        <AppHeader />
        <div className="flex min-h-0 flex-1 items-center justify-center p-10 text-sm text-destructive">
          Некорректный идентификатор активиста.
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col" data-testid="activist-card">
      <AppHeader />

      {/* Sub-header */}
      <div
        className="flex items-center justify-between border-b bg-[var(--panel)]"
        style={{ padding: "12px 22px" }}
      >
        <div className="flex items-center" style={{ gap: 10 }}>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1 text-sm font-medium"
            style={{ color: "var(--accent-hex)" }}
          >
            <ArrowLeft className="h-4 w-4" />
            Активисты
          </button>
          <span className="text-sm text-muted-foreground">/</span>
          <span className="text-sm text-muted-foreground">Карточка</span>
        </div>
        {/* F50: удалены нефункциональные кнопки «В касание» / «Редактировать» (были без
            onClick — молча ничего не делали). Карточка активиста — read-only просмотр;
            наполнение касаний идёт через выбор на «Активистах» + корзину касания. */}
      </div>

      {/* Body */}
      <div className="min-h-0 flex-1 overflow-auto" style={{ padding: 22 }}>
        {/* When the row object came via router-state, render immediately; the
            list query is disabled, so only its loading/error states for a cold
            entry (direct URL / reload) gate the UI here. */}
        {!stateVolunteer && volunteerQuery.isPending ? (
          <div className="flex items-center justify-center py-20 text-sm text-muted-foreground">
            <Spinner className="mr-2 text-muted-foreground" />
            Загрузка карточки…
          </div>
        ) : !stateVolunteer && volunteerQuery.isError ? (
          <div className="py-20 text-center text-sm text-destructive">
            Не удалось загрузить карточку активиста.{" "}
            {volunteerQuery.error instanceof Error ? volunteerQuery.error.message : ""}
          </div>
        ) : !volunteer ? (
          <div className="py-20 text-center text-sm text-muted-foreground">
            Активист не найден.
          </div>
        ) : (
          <div
            className="grid"
            style={{ gridTemplateColumns: "1fr 380px", gap: 20, maxWidth: 1180 }}
          >
            {/* Left column: Identity / Contacts / Binding / Flags */}
            <div className="flex flex-col" style={{ gap: 16 }}>
              <IdentityCard volunteer={volunteer} refs={refs} />
              <ContactsCard volunteer={volunteer} />
              <BindingCard volunteer={volunteer} />
              <FlagsCard volunteer={volunteer} refs={refs} />
            </div>

            {/* Right column: touch list + Summary */}
            <div className="flex flex-col" style={{ gap: 16 }}>
              <HistoryTimeline
                touches={touches}
                count={touchIds.length}
                pending={touchIds.length > 0 && touchesQuery.isPending}
                error={touchesQuery.isError}
              />
              {!touchesQuery.isError && (
                <HistorySummary touches={touches} count={touchIds.length} known={touchIds.length === 0 || !touchesQuery.isPending} />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Left column ───────────────────────────────────────────────────────── */

function IdentityCard({
  volunteer,
  refs,
}: {
  volunteer: Volunteer;
  refs: Refs | undefined;
}): JSX.Element {
  const verified = raw(volunteer, "verified") === true || raw(volunteer, "verified") === "y";
  const readiness = pick(volunteer as Record<string, unknown>, ["readiness_region", "readiness"]);
  const loyalty = volunteer.loyalty ?? null;
  const membership = membershipLabel(volunteer, refs);

  return (
    <div style={{ ...CARD_STYLE, padding: "20px 22px" }}>
      <div className="flex items-center" style={{ gap: 16 }}>
        <div
          className="flex items-center justify-center"
          style={{
            width: 60,
            height: 60,
            borderRadius: "50%",
            background: "var(--accent-avatar)",
            color: "var(--accent-hex)",
            fontSize: 22,
            fontWeight: 600,
            flex: "0 0 auto",
          }}
        >
          {initialsOf(volunteer)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="flex items-center" style={{ gap: 10, flexWrap: "wrap" }}>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: "-.01em" }}>
              {fullName(volunteer)}
            </h1>
            {verified && (
              <Pill tone="green" style={{ fontSize: 11, fontWeight: 600, padding: "3px 8px" }}>
                ✓ Верифицирован
              </Pill>
            )}
          </div>
          <div style={{ fontFamily: MONO, fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>
            ID {volunteer.id} · добавлен {fmtDate(raw(volunteer, "created_at"))}
          </div>
        </div>
      </div>

      <div className="flex" style={{ gap: 8, marginTop: 16, flexWrap: "wrap" }}>
        {readiness !== "—" && (
          <Pill
            tone={READINESS_TONE[readiness] ?? "gray"}
            withDot
            style={{ fontSize: 12, padding: "4px 10px", borderRadius: 7 }}
          >
            Готовность: {readiness}
          </Pill>
        )}
        {loyalty && (
          <Pill
            tone={loyaltyTone(loyalty)}
            style={{ fontSize: 12, padding: "4px 10px", borderRadius: 7 }}
          >
            Лояльность: {loyalty}
          </Pill>
        )}
        {membership !== "—" && (
          <Pill tone="blue" style={{ fontSize: 12, padding: "4px 10px", borderRadius: 7 }}>
            {membership}
          </Pill>
        )}
      </div>
    </div>
  );
}

/** A label cell in a 150px/1fr definition grid. */
function Label({ children }: { children: React.ReactNode }): JSX.Element {
  return <span style={{ color: "var(--text-muted)", padding: "7px 0" }}>{children}</span>;
}

/** A value cell with the standard var(--slate-bg) bottom divider unless `last`. */
function Value({
  children,
  last = false,
  style,
}: {
  children: React.ReactNode;
  last?: boolean;
  style?: React.CSSProperties;
}): JSX.Element {
  return (
    <span
      style={{
        padding: "7px 0",
        borderBottom: last ? undefined : "1px solid var(--slate-bg)",
        ...style,
      }}
    >
      {children}
    </span>
  );
}

function DefGrid({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "150px 1fr",
        gap: "2px 16px",
        fontSize: 13,
      }}
    >
      {children}
    </div>
  );
}

function ContactsCard({ volunteer }: { volunteer: Volunteer }): JSX.Element {
  const phone = pick(volunteer as Record<string, unknown>, ["phone_mobile"]);
  const email = pick(volunteer as Record<string, unknown>, ["email"]);
  const vk = pick(volunteer as Record<string, unknown>, ["vk", "vk_url", "vk_link"]);
  const tg = pick(volunteer as Record<string, unknown>, ["telegram", "tg", "tg_username"]);

  return (
    <div style={{ ...CARD_STYLE, padding: "6px 22px 14px" }}>
      <div style={SECTION_LABEL_STYLE}>Контакты</div>
      <DefGrid>
        <Label>Телефон</Label>
        <Value style={{ fontFamily: MONO }}>{phone}</Value>
        <Label>Email</Label>
        <Value>{email}</Value>
        <Label>VK</Label>
        <Value style={{ color: vk !== "—" ? "var(--accent-hex)" : undefined }}>{vk}</Value>
        <Label>Telegram</Label>
        <Value last style={{ color: tg !== "—" ? "var(--accent-hex)" : undefined }}>
          {tg}
        </Value>
      </DefGrid>
    </div>
  );
}

/** chip with var(--slate-bg) background (cells, neutral tokens). */
function ChipNeutral({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <span
      style={{
        background: "var(--slate-bg)",
        color: "var(--text-2)",
        fontSize: 12,
        padding: "2px 8px",
        borderRadius: 6,
      }}
    >
      {children}
    </span>
  );
}

function BindingCard({ volunteer }: { volunteer: Volunteer }): JSX.Element {
  const branch = pick(volunteer as Record<string, unknown>, ["branch_name"]);
  const primary = pick(volunteer as Record<string, unknown>, ["primary_name"]);
  const uik = pick(volunteer as Record<string, unknown>, ["uik_n", "uik_id"]);
  const cells = toList(raw(volunteer, "cells"));

  return (
    <div style={{ ...CARD_STYLE, padding: "6px 22px 16px" }}>
      <div style={SECTION_LABEL_STYLE}>Привязка</div>
      <DefGrid>
        <Label>Отделение</Label>
        <Value>{branch}</Value>
        <Label>Первичное</Label>
        <Value>{primary}</Value>
        <Label>УИК</Label>
        <Value style={{ fontFamily: MONO }}>{uik}</Value>
        <Label>Ячейки</Label>
        <Value last style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {cells.length ? cells.map((c, i) => <ChipNeutral key={i}>{c}</ChipNeutral>) : "—"}
        </Value>
      </DefGrid>
    </div>
  );
}

function FlagsCard({
  volunteer,
  refs,
}: {
  volunteer: Volunteer;
  refs: Refs | undefined;
}): JSX.Element {
  const statuses = toList(raw(volunteer, "status"));
  const groups = targetGroupLabels(volunteer, refs);

  return (
    <div style={{ ...CARD_STYLE, padding: "6px 22px 16px" }}>
      <div style={SECTION_LABEL_STYLE}>Признаки</div>
      <DefGrid>
        <Label>Статус</Label>
        <Value style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {statuses.length
            ? statuses.map((s, i) => (
                <span
                  key={i}
                  style={{
                    background: "var(--accent-weak)",
                    color: "var(--accent-weak-text)",
                    fontSize: 12,
                    padding: "2px 8px",
                    borderRadius: 6,
                  }}
                >
                  {s}
                </span>
              ))
            : "—"}
        </Value>
        <Label>Целевые группы</Label>
        <Value style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {groups.length
            ? groups.map((g, i) => (
                <span
                  key={i}
                  style={{
                    background: "var(--tone-violet-bg)",
                    color: "var(--tone-violet-fg)",
                    fontSize: 12,
                    padding: "2px 8px",
                    borderRadius: 6,
                  }}
                >
                  {g}
                </span>
              ))
            : "—"}
        </Value>
        <Label>Лоялен партии</Label>
        <Value>{yesNo(raw(volunteer, "is_party_loyal"))}</Value>
        <Label>Лоялен президенту</Label>
        <Value last>{yesNo(raw(volunteer, "is_president_loyal"))}</Value>
      </DefGrid>
    </div>
  );
}

/* ─── Right column ──────────────────────────────────────────────────────── */

function HistoryTimeline({
  touches,
  count,
  pending,
  error,
}: {
  touches: Touch[];
  count: number;
  pending: boolean;
  error: boolean;
}): JSX.Element {
  // Newest first by start date; undated sink to the bottom.
  const sorted = [...touches].sort((a, b) => startMs(b) - startMs(a));

  return (
    <div style={{ ...CARD_STYLE, padding: "16px 18px" }}>
      <div
        className="flex items-center justify-between"
        style={{ marginBottom: sorted.length ? 14 : 6 }}
      >
        <span style={{ fontSize: 14, fontWeight: 600 }}>История касаний</span>
        <span style={{ fontFamily: MONO, fontSize: 12, color: "var(--text-muted)" }}>{count}</span>
      </div>

      {error ? (
        <div style={{ fontSize: 13, padding: "10px 0 14px" }} className="text-destructive">
          Не удалось загрузить историю касаний.
        </div>
      ) : pending ? (
        <div className="flex items-center py-6 text-sm text-muted-foreground">
          <Spinner className="mr-2 text-muted-foreground" />
          Загрузка истории…
        </div>
      ) : sorted.length === 0 ? (
        <div style={{ fontSize: 13, color: "var(--text-muted)", padding: "10px 0 14px" }}>
          Пока нет касаний.
        </div>
      ) : (
        sorted.map((t, i) => {
          // Neutral dot, optionally tinted by activity (green=active, gray=other).
          const dot = t.is_active === true ? "var(--success-dot)" : "var(--gray-dot)";
          const last = i === sorted.length - 1;
          const name = String(t.name ?? "").trim() || "Касание";
          const type =
            t.touch_type != null && t.touch_type !== "" ? String(t.touch_type) : "";
          const period = fmtPeriod(t.date_start, t.date_end);
          return (
            <div
              key={t.id}
              className="flex"
              style={{ gap: 12, paddingBottom: last ? 12 : 14, position: "relative" }}
            >
              <div className="flex flex-col items-center">
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: dot,
                    border: "2px solid var(--panel)",
                    boxShadow: `0 0 0 1.5px ${dot}`,
                    flex: "0 0 auto",
                  }}
                />
                {!last && (
                  <span style={{ flex: 1, width: 1.5, background: "var(--border-soft2)", marginTop: 2 }} />
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0, paddingBottom: 2 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-strong)" }}>{name}</div>
                <div className="flex items-center" style={{ gap: 8, marginTop: 3, flexWrap: "wrap" }}>
                  {type && (
                    <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{type}</span>
                  )}
                  {period !== "—" && (
                    <span style={{ fontFamily: MONO, fontSize: 11, color: "var(--text-muted)" }}>
                      {period}
                    </span>
                  )}
                  <span style={{ fontFamily: MONO, fontSize: 11, color: "var(--control-border)" }}>
                    #{t.id}
                  </span>
                </div>
              </div>
            </div>
          );
        })
      )}

      {/* F50: убрана нефункциональная кнопка «＋ Добавить в касание» (была без onClick). */}
    </div>
  );
}

function HistorySummary({
  touches,
  count,
  known,
}: {
  touches: Touch[];
  count: number;
  /** False while the touch list is still loading — show "—" rather than 0. */
  known: boolean;
}): JSX.Element {
  // "Active" is only meaningful for the touches actually resolved; surface it
  // only when at least one of them carries an is_active flag (no invention).
  const hasActiveFlag = touches.some((t) => t.is_active != null);
  const active = known && hasActiveFlag ? touches.filter((t) => t.is_active === true).length : null;

  return (
    <div style={{ ...CARD_STYLE, padding: "16px 18px" }}>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Сводка</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <SummaryTile value={known ? count : null} color="var(--accent-deep)" label="всего касаний" />
        {hasActiveFlag && <SummaryTile value={active} color="var(--success)" label="активных" />}
      </div>
    </div>
  );
}

function SummaryTile({
  value,
  color,
  label,
}: {
  value: number | null;
  color: string;
  label: string;
}): JSX.Element {
  return (
    <div style={{ background: "var(--panel-subtle)", borderRadius: 10, padding: 12 }}>
      <div style={{ fontFamily: MONO, fontSize: 22, fontWeight: 600, color }}>
        {value == null ? "—" : value}
      </div>
      <div style={{ fontSize: 11.5, color: "var(--text-3)", marginTop: 2 }}>{label}</div>
    </div>
  );
}
